import React, { useEffect, useMemo, useState } from 'react';
import { Header, Container, Table, Button, Form, Segment, Dropdown, Message, Icon, Checkbox } from 'semantic-ui-react';
import { useToast } from '../../hooks/useToast';
import { useTeamsQuery } from '../../hooks/teams/useTeams';
import { isCqvKind, TeamListDto } from '../../services/teams/teamService';
import {
  cqvScheduleService,
  CqvScheduleResponseDto,
  EDayOfWeek,
  DAY_OF_WEEK_LABELS,
  API_DAY_OF_WEEK_LABELS,
} from '../../services/contracts/CqvScheduleService';
import { extractApiErrorMessage } from '../../services/utils/apiErrorUtils';

const ALL_DAYS = [
  EDayOfWeek.Domingo,
  EDayOfWeek.Segunda,
  EDayOfWeek.Terca,
  EDayOfWeek.Quarta,
  EDayOfWeek.Quinta,
  EDayOfWeek.Sexta,
  EDayOfWeek.Sabado,
];

type UserOption = { id: string; nome: string };

/**
 * Escala de quem faz CQV por período — specs/solicitacao-cqv-automatica-sla (AC#10).
 * Separada da configuração de quem faz VTME: nem sempre são as mesmas pessoas.
 * Usada pelo CqvSlaAlertJob pra @mencionar quem está de plantão no alerta de atraso.
 */
const CqvSchedulePage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { data: teamsData, isLoading: teamsLoading, isError: teamsError } = useTeamsQuery();
  const teams: TeamListDto[] | undefined = teamsData;

  const [schedules, setSchedules] = useState<CqvScheduleResponseDto[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const loading = teamsLoading || schedulesLoading;
  const [saving, setSaving] = useState(false);

  const [userId, setUserId] = useState('');
  const [selectedDays, setSelectedDays] = useState<Set<EDayOfWeek>>(new Set());
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('18:00');
  const [coversGcm, setCoversGcm] = useState(false);

  // Edição inline de uma escala já existente — só horário/GCM (dia e usuário não mudam).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editStart, setEditStart] = useState('08:00');
  const [editEnd, setEditEnd] = useState('18:00');
  const [editGcm, setEditGcm] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const loadSchedules = async () => {
    try {
      const data = await cqvScheduleService.list();
      setSchedules(data);
    } catch (err) {
      console.error('Erro ao carregar escala de CQV:', err);
      showError('Não foi possível carregar a escala de CQV.');
    }
  };

  useEffect(() => {
    (async () => {
      setSchedulesLoading(true);
      try {
        await loadSchedules();
      } finally {
        setSchedulesLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (teamsError) {
      showError('Não foi possível carregar a lista de usuários do time de CQV.');
    }
  }, [teamsError, showError]);

  useEffect(() => {
    if (!teams) return;

    // Só quem está no time de CQV ou lidera ele — não a lista inteira de usuários do sistema.
    const cqvTeams = teams.filter((t) => isCqvKind(t.kind));
    const byId = new Map<string, UserOption>();
    for (const team of cqvTeams) {
      if (team.managerId && team.managerName) {
        byId.set(team.managerId, { id: team.managerId, nome: team.managerName });
      }
      for (const member of team.salespersons ?? []) {
        byId.set(member.userId, { id: member.userId, nome: member.userName });
      }
    }
    setUsers(Array.from(byId.values()).sort((a, b) => a.nome.localeCompare(b.nome)));
  }, [teams]);

  const userOptions = useMemo(() => users.map((u) => ({ key: u.id, value: u.id, text: u.nome })), [users]);

  const allDaysSelected = selectedDays.size === ALL_DAYS.length;

  const toggleDay = (day: EDayOfWeek) => {
    setSelectedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  const toggleAllDays = () => {
    setSelectedDays(allDaysSelected ? new Set() : new Set(ALL_DAYS));
  };

  const handleCreate = async () => {
    if (!userId) {
      showError('Selecione um usuário.');
      return;
    }
    if (selectedDays.size === 0) {
      showError('Selecione ao menos um dia da semana.');
      return;
    }
    setSaving(true);
    try {
      await cqvScheduleService.createBulk({
        userId,
        daysOfWeek: Array.from(selectedDays),
        startTime,
        endTime,
        coversGcm,
      });
      showSuccess(
        selectedDays.size === 1 ? 'Escala adicionada.' : `Escala adicionada em ${selectedDays.size} dias.`
      );
      setUserId('');
      setSelectedDays(new Set());
      await loadSchedules();
    } catch (err) {
      showError(extractApiErrorMessage(err, 'Não foi possível salvar a escala.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await cqvScheduleService.deactivate(id);
      showSuccess('Escala removida.');
      await loadSchedules();
    } catch (err) {
      showError(extractApiErrorMessage(err, 'Não foi possível remover a escala.'));
    }
  };

  const startEdit = (s: CqvScheduleResponseDto) => {
    setEditingId(s.id);
    setEditStart(s.startTime.slice(0, 5));
    setEditEnd(s.endTime.slice(0, 5));
    setEditGcm(s.coversGcm);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (id: string) => {
    setEditSaving(true);
    try {
      await cqvScheduleService.update(id, { startTime: editStart, endTime: editEnd, coversGcm: editGcm });
      showSuccess('Escala atualizada.');
      setEditingId(null);
      await loadSchedules();
    } catch (err) {
      showError(extractApiErrorMessage(err, 'Não foi possível atualizar a escala.'));
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <Container style={{ paddingTop: 24, paddingBottom: 48 }}>
      <Header as="h2">Escala de CQV</Header>
      <p style={{ color: '#666', marginBottom: 20 }}>
        Quem faz a checagem de qualidade da ligação de venda (CQV) e em qual horário — separado
        de quem faz VTME. Usado pelo alerta de SLA pra saber quem @mencionar quando um pedido
        estoura o prazo de 10 minutos. Lista de usuários restrita a quem está ou lidera o time de CQV.
      </p>

      <Segment>
        <Header as="h4">Adicionar escala</Header>
        <Form loading={saving}>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Usuário</label>
              <Dropdown
                selection
                search
                placeholder={users.length === 0 && !loading ? 'Nenhum usuário no time de CQV' : 'Selecionar usuário'}
                options={userOptions}
                value={userId}
                onChange={(_, { value }) => setUserId(String(value))}
                loading={loading}
                disabled={users.length === 0 && !loading}
              />
            </Form.Field>
            <Form.Input
              label="Início"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
            <Form.Input
              label="Fim"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </Form.Group>

          <Form.Field>
            <label>Dias da semana</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center' }}>
              <Checkbox
                label={<label style={{ fontWeight: 600 }}>Todos os dias</label>}
                checked={allDaysSelected}
                onChange={toggleAllDays}
              />
              <div style={{ width: 1, alignSelf: 'stretch', background: '#ddd' }} />
              {ALL_DAYS.map((day) => (
                <Checkbox
                  key={day}
                  label={DAY_OF_WEEK_LABELS[day]}
                  checked={selectedDays.has(day)}
                  onChange={() => toggleDay(day)}
                />
              ))}
            </div>
          </Form.Field>

          <Form.Field>
            <Checkbox
              label="Também cobre CQV do pipeline GCM"
              checked={coversGcm}
              onChange={(_, { checked }) => setCoversGcm(!!checked)}
            />
          </Form.Field>
          <Button primary onClick={handleCreate} loading={saving} disabled={saving}>
            <Icon name="plus" />
            Adicionar {selectedDays.size > 1 ? `(${selectedDays.size} dias)` : ''}
          </Button>
        </Form>
      </Segment>

      <Segment>
        <Header as="h4">Escala atual</Header>
        {schedules.length === 0 ? (
          <Message info>Nenhuma escala cadastrada ainda — o alerta de SLA sai sem @menção até ter alguém aqui.</Message>
        ) : (
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Usuário</Table.HeaderCell>
                <Table.HeaderCell>Dia</Table.HeaderCell>
                <Table.HeaderCell>Início</Table.HeaderCell>
                <Table.HeaderCell>Fim</Table.HeaderCell>
                <Table.HeaderCell>GCM</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {schedules.map((s) => {
                const isEditing = editingId === s.id;
                return (
                  <Table.Row key={s.id} disabled={!s.isActive && !isEditing}>
                    <Table.Cell>{s.userNome}</Table.Cell>
                    <Table.Cell>{API_DAY_OF_WEEK_LABELS[s.dayOfWeek] ?? s.dayOfWeek}</Table.Cell>
                    {isEditing ? (
                      <>
                        <Table.Cell>
                          <input
                            type="time"
                            value={editStart}
                            onChange={(e) => setEditStart(e.target.value)}
                            style={{ width: '100%' }}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <input
                            type="time"
                            value={editEnd}
                            onChange={(e) => setEditEnd(e.target.value)}
                            style={{ width: '100%' }}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Checkbox checked={editGcm} onChange={(_, { checked }) => setEditGcm(!!checked)} />
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            size="tiny"
                            color="green"
                            onClick={() => saveEdit(s.id)}
                            loading={editSaving}
                            disabled={editSaving}
                            style={{ marginRight: 6 }}
                          >
                            Salvar
                          </Button>
                          <Button size="tiny" basic onClick={cancelEdit} disabled={editSaving}>
                            Cancelar
                          </Button>
                        </Table.Cell>
                      </>
                    ) : (
                      <>
                        <Table.Cell>{s.startTime.slice(0, 5)}</Table.Cell>
                        <Table.Cell>{s.endTime.slice(0, 5)}</Table.Cell>
                        <Table.Cell>{s.coversGcm ? <Icon name="check" color="green" /> : '—'}</Table.Cell>
                        <Table.Cell>
                          {s.isActive ? (
                            <>
                              <Button size="tiny" basic onClick={() => startEdit(s)} style={{ marginRight: 6 }}>
                                <Icon name="edit" />
                                Editar
                              </Button>
                              <Button size="tiny" color="red" basic onClick={() => handleDeactivate(s.id)}>
                                Remover
                              </Button>
                            </>
                          ) : (
                            <span style={{ color: '#999' }}>Inativa</span>
                          )}
                        </Table.Cell>
                      </>
                    )}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </Segment>
    </Container>
  );
};

export default CqvSchedulePage;
